import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Select, type SelectOption } from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { INPUT_CLS } from "@/lib/analysis";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/i18n";
import { Navigate, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

/**
 * `/agents/log-triage/sources` — admin-only. Config for the log-triage sweep: which
 * Grafana/Loki servers to poll and which container maps to which project.
 * The poll itself is a periodic worker job (server/workers/jobs/log-triage.ts)
 * — this page only edits config, it never triggers a run.
 */

type Project = { id: string; name: string };
type ContainerMap = {
  id: string;
  containerPattern: string;
  projectId: string;
  enabled: boolean;
  project: { id: string; name: string };
};
type DiscoveryReport = {
  hosts: Array<{
    host: string;
    created: boolean;
    error?: string;
    mapped: Array<{ service: string; project: string; existed: boolean }>;
    unmatched: Array<{ service: string; reason: string }>;
    skipped: number;
  }>;
};
type LogSourceRow = {
  id: string;
  name: string;
  slug: string;
  grafanaUrl: string;
  logqlSelector: string;
  errorFilterLogql: string | null;
  lokiDatasourceUid: string | null;
  tokenEnvVar: string;
  enabled: boolean;
  fallbackProjectId: string | null;
  fallbackProject: { id: string; name: string } | null;
  checkpointAt: string | null;
  lastPolledAt: string | null;
  containerMaps: ContainerMap[];
};

const emptyForm = {
  name: "",
  slug: "",
  grafanaUrl: "",
  logqlSelector: "",
  errorFilterLogql: "",
  lokiDatasourceUid: "",
  tokenEnvVar: "",
  fallbackProjectId: "",
};

export function LogSourcesPage() {
  const { t } = useTranslation();
  const { user } = useLoaderData({ from: "/_app" });
  const isAdmin = user.globalRole === "admin";
  const [sources, setSources] = useState<LogSourceRow[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [discover, setDiscover] = useState({
    grafanaUrl: "",
    tokenEnvVar: "",
    lokiDatasourceUid: "",
  });
  const [discovering, setDiscovering] = useState(false);
  const [report, setReport] = useState<DiscoveryReport | null>(null);

  const load = useCallback(
    () =>
      api
        .get<{ sources: LogSourceRow[] }>("/api/log-sources")
        .then((r) => setSources(r.sources))
        .catch(() => {}),
    [],
  );

  useEffect(() => {
    if (!isAdmin) return;
    void load();
    api
      .get<{ projects: Project[] }>("/api/projects?pageSize=200")
      .then((r) => setProjects(r.projects.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {});
  }, [isAdmin, load]);

  if (!isAdmin) return <Navigate to="/" />;

  async function createSource() {
    setFormError(null);
    try {
      await api.post("/api/log-sources", {
        name: form.name.trim(),
        slug: form.slug.trim(),
        grafanaUrl: form.grafanaUrl.trim(),
        logqlSelector: form.logqlSelector.trim(),
        errorFilterLogql: form.errorFilterLogql.trim() || null,
        lokiDatasourceUid: form.lokiDatasourceUid.trim() || null,
        tokenEnvVar: form.tokenEnvVar.trim(),
        fallbackProjectId: form.fallbackProjectId || null,
      });
      setForm(emptyForm);
      setOpen(false);
      await load();
    } catch {
      setFormError(t("agentTabs.logSources.createFailed"));
    }
  }

  async function runDiscovery() {
    setDiscovering(true);
    setReport(null);
    try {
      const r = await api.post<DiscoveryReport>("/api/log-sources/discover", {
        grafanaUrl: discover.grafanaUrl.trim(),
        tokenEnvVar: discover.tokenEnvVar.trim(),
        lokiDatasourceUid: discover.lokiDatasourceUid.trim() || null,
      });
      setReport(r);
      await load();
    } catch (err) {
      failed(t("agentTabs.logSources.what.discover"))(err);
    } finally {
      setDiscovering(false);
    }
  }

  async function toggleEnabled(source: LogSourceRow) {
    setSources((prev) => prev.map((s) => (s.id === source.id ? { ...s, enabled: !s.enabled } : s)));
    await api
      .patch(`/api/log-sources/${source.id}`, { enabled: !source.enabled })
      .catch(() => void load());
  }

  /** A failed write is said out loud, never left as a row that quietly
   *  stayed on screen. */
  function failed(what: string) {
    return (err: unknown) =>
      toast({
        title: t("agentTabs.logSources.failed", { what }),
        description: err instanceof Error ? err.message : t("agentTabs.logSources.error"),
        variant: "destructive",
      });
  }

  async function deleteSource(id: string) {
    await api.delete(`/api/log-sources/${id}`).catch(failed(t("agentTabs.logSources.what.delete")));
    await load();
  }

  async function setFallback(source: LogSourceRow, fallbackProjectId: string | null) {
    setSources((prev) => prev.map((s) => (s.id === source.id ? { ...s, fallbackProjectId } : s)));
    await api
      .patch(`/api/log-sources/${source.id}`, { fallbackProjectId })
      .catch(() => void load());
  }

  async function addMap(source: LogSourceRow, containerPattern: string, projectId: string) {
    if (!containerPattern.trim() || !projectId) return;
    await api
      .post(`/api/log-sources/${source.id}/container-maps`, {
        containerPattern: containerPattern.trim(),
        projectId,
      })
      .catch(failed(t("agentTabs.logSources.what.addMap")));
    await load();
  }

  async function removeMap(source: LogSourceRow, mapId: string) {
    await api
      .delete(`/api/log-sources/${source.id}/container-maps/${mapId}`)
      .catch(failed(t("agentTabs.logSources.what.removeMap")));
    await load();
  }

  const projectOptions: SelectOption[] = projects.map((p) => ({ value: p.id, label: p.name }));

  return (
    <div className="w-full space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">{t("agentTabs.logSources.title")}</h2>
          <p className="text-muted-foreground mt-1">{t("agentTabs.logSources.intro")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={discoverOpen} onOpenChange={setDiscoverOpen}>
            <DialogTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid="discover-log-sources-open"
              >
                {t("agentTabs.logSources.discoverOpen")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{t("agentTabs.logSources.discoverOpen")}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                <Trans i18nKey="agentTabs.logSources.discoverHint" components={{ c: <code /> }} />
              </p>
              <div className="space-y-2">
                <input
                  className={INPUT_CLS}
                  placeholder={t("agentTabs.logSources.ph.grafanaUrl")}
                  value={discover.grafanaUrl}
                  onChange={(e) => setDiscover((d) => ({ ...d, grafanaUrl: e.target.value }))}
                />
                <input
                  className={INPUT_CLS}
                  placeholder={t("agentTabs.logSources.ph.tokenEnvDiscover")}
                  value={discover.tokenEnvVar}
                  onChange={(e) => setDiscover((d) => ({ ...d, tokenEnvVar: e.target.value }))}
                />
                <input
                  className={INPUT_CLS}
                  placeholder={t("agentTabs.logSources.ph.lokiUidShort")}
                  value={discover.lokiDatasourceUid}
                  onChange={(e) =>
                    setDiscover((d) => ({ ...d, lokiDatasourceUid: e.target.value }))
                  }
                />
                <Button
                  type="button"
                  onClick={runDiscovery}
                  disabled={discovering}
                  data-testid="discover-log-sources-run"
                >
                  {discovering
                    ? t("agentTabs.logSources.discovering")
                    : t("agentTabs.logSources.discoverRun")}
                </Button>
                {report && (
                  <div className="space-y-2 text-sm" data-testid="discover-log-sources-report">
                    {report.hosts.map((h) => (
                      <div key={h.host}>
                        <div className="font-medium">
                          {h.error
                            ? t("agentTabs.logSources.hostError", { host: h.host, error: h.error })
                            : t("agentTabs.logSources.hostSummary", {
                                host: h.host,
                                mapped: h.mapped.length,
                                unmatched: h.unmatched.length,
                                skipped: h.skipped,
                              })}
                        </div>
                        {h.unmatched.length > 0 && (
                          <ul className="ml-4 list-disc text-muted-foreground">
                            {h.unmatched.map((u) => (
                              <li key={u.service}>
                                {u.service}: {u.reason}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button type="button" size="sm" data-testid="new-log-source-open">
                {t("agentTabs.logSources.newSource")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{t("agentTabs.logSources.newTitle")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-2">
                <input
                  className={INPUT_CLS}
                  placeholder={t("agentTabs.logSources.ph.name")}
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
                <input
                  className={INPUT_CLS}
                  placeholder={t("agentTabs.logSources.ph.slug")}
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                />
                <input
                  className={INPUT_CLS}
                  placeholder={t("agentTabs.logSources.ph.grafanaUrl")}
                  value={form.grafanaUrl}
                  onChange={(e) => setForm((f) => ({ ...f, grafanaUrl: e.target.value }))}
                />
                <input
                  className={INPUT_CLS}
                  placeholder={t("agentTabs.logSources.ph.selector")}
                  value={form.logqlSelector}
                  onChange={(e) => setForm((f) => ({ ...f, logqlSelector: e.target.value }))}
                />
                <input
                  className={INPUT_CLS}
                  placeholder={t("agentTabs.logSources.ph.errorFilter")}
                  value={form.errorFilterLogql}
                  onChange={(e) => setForm((f) => ({ ...f, errorFilterLogql: e.target.value }))}
                />
                <input
                  className={INPUT_CLS}
                  placeholder={t("agentTabs.logSources.ph.lokiUid")}
                  value={form.lokiDatasourceUid}
                  onChange={(e) => setForm((f) => ({ ...f, lokiDatasourceUid: e.target.value }))}
                />
                <input
                  className={INPUT_CLS}
                  placeholder={t("agentTabs.logSources.ph.tokenEnv")}
                  value={form.tokenEnvVar}
                  onChange={(e) => setForm((f) => ({ ...f, tokenEnvVar: e.target.value }))}
                />
                <Select
                  value={form.fallbackProjectId}
                  onChange={(v) => setForm((f) => ({ ...f, fallbackProjectId: v }))}
                  options={[
                    { value: "", label: t("agentTabs.logSources.noFallbackLong") },
                    ...projectOptions,
                  ]}
                />
                {formError && <p className="text-sm text-destructive">{formError}</p>}
                <Button type="button" onClick={createSource} data-testid="create-log-source">
                  {t("agentTabs.logSources.create")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {sources.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="log-sources-empty">
          {t("agentTabs.logSources.empty")}
        </p>
      ) : (
        <ul className="space-y-4">
          {sources.map((s) => (
            <li key={s.id} className="rounded-md border p-4 space-y-3" data-testid="log-source-row">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <span className="font-medium">{s.name}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{s.slug}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{s.grafanaUrl}</span>
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={s.enabled}
                      onChange={() => toggleEnabled(s)}
                      data-testid="log-source-enabled"
                    />
                    {t("agentTabs.logSources.enabled")}
                  </label>
                  <Select
                    className="w-56"
                    value={s.fallbackProjectId ?? ""}
                    onChange={(v) => setFallback(s, v || null)}
                    aria-label={t("agentTabs.logSources.fallbackFor", { name: s.name })}
                    options={[
                      { value: "", label: t("agentTabs.logSources.noFallback") },
                      ...projectOptions,
                    ]}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteSource(s.id)}
                  >
                    {t("agentTabs.logSources.delete")}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {s.checkpointAt
                  ? t("agentTabs.logSources.checkedUpTo", { date: formatDateTime(s.checkpointAt) })
                  : t("agentTabs.logSources.notPolled")}
              </p>
              <ContainerMaps
                source={s}
                projectOptions={projectOptions}
                onAdd={(pattern, projectId) => addMap(s, pattern, projectId)}
                onRemove={(mapId) => removeMap(s, mapId)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ContainerMaps({
  source,
  projectOptions,
  onAdd,
  onRemove,
}: {
  source: LogSourceRow;
  projectOptions: SelectOption[];
  onAdd: (pattern: string, projectId: string) => void;
  onRemove: (mapId: string) => void;
}) {
  const { t } = useTranslation();
  const [pattern, setPattern] = useState("");
  const [projectId, setProjectId] = useState("");
  return (
    <div className="space-y-2 border-t pt-3">
      <p className="text-xs font-medium text-muted-foreground">
        {t("agentTabs.logSources.containerToProject")}
      </p>
      {source.containerMaps.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("agentTabs.logSources.noMaps")}</p>
      ) : (
        <ul className="space-y-1">
          {source.containerMaps.map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-2 text-sm">
              <span>
                <code>{m.containerPattern}</code> → {m.project.name}
              </span>
              <Button type="button" variant="ghost" size="sm" onClick={() => onRemove(m.id)}>
                {t("agentTabs.logSources.remove")}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <input
          className={`${INPUT_CLS} w-48`}
          placeholder={t("agentTabs.logSources.ph.container")}
          value={pattern}
          onChange={(e) => setPattern(e.target.value)}
        />
        <Select
          className="w-48"
          value={projectId}
          onChange={setProjectId}
          options={[
            { value: "", label: t("agentTabs.logSources.selectProject") },
            ...projectOptions,
          ]}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            onAdd(pattern, projectId);
            setPattern("");
            setProjectId("");
          }}
        >
          {t("agentTabs.logSources.add")}
        </Button>
      </div>
    </div>
  );
}
